import type { Partner } from "@expert/shared-types";

import { getAppConfigSingleton } from "@expert/app-config";
import { sendEventMetric, withMetrics } from "@expert/cloudwatch-metrics";
import { getLogger } from "@expert/logging";

import type { Session } from "@/sdk/sessions/types";

import { isVoiceTask } from "@/sdk/agent/voice";
import { useSessionStore } from "@/sdk/sessions/session.store";
import { isBlocklistedNumber } from "@/shared-utils";

import { linkCustomerAndGetHistory, toC360Mdn } from "./api/interactions";
import { getC360Line, isC360FlagOn, metricDimensions } from "./c360InteractionLifecycle";
import { useC360CustomerStore } from "./store/useC360CustomerStore";

const logger = getLogger({ module: "c360" });

/** What a lookup's number came from, sent as the event's `source`: the IVR at call start, or the expert's confirm. */
type C360LookupSource = "ivr" | "confirm";

/**
 * At call start, looks the caller's number from the IVR up in C360, which links the line and returns the customer's
 * history. Only a recorded call has a C360 line, so recording's flag decides whether this runs. Never throws.
 */
export async function linkC360CustomerAtCallStart(session: Session, partner: Partner): Promise<void> {
    try {
        const task = session.currentTask;
        if (!isVoiceTask(task)) return;
        // Checked first so a skip below is only counted for a recorded call.
        if (!(await getC360Line(session.id))) return;
        // On a transferred leg the task's number can be a trunk or toll-free ANI, which the partner blocklists so the
        // customer tab hides it too; looking one up could link every call on that trunk to whoever owns the number.
        const { blocklistedNumbers, blocklistedPrefixes } = getAppConfigSingleton().getPartnersConfig(partner);
        if (isBlocklistedNumber(task.mdn, blocklistedNumbers, blocklistedPrefixes)) {
            recordSkippedLookup(session.id, partner, "blocklisted");
            return;
        }
        const mdn = toC360Mdn(task.mdn);
        if (!mdn) {
            recordSkippedLookup(session.id, partner, "no_mdn");
            return;
        }
        await linkCustomerAndLoadHistory(session.id, partner, "ivr", mdn);
    } catch (error) {
        logger.warn({ error, sessionId: session.id }, "C360 customer link failed");
    }
}

/**
 * The expert confirmed the customer in Sales' verification panel: looks up again when the number or name changed, which
 * also puts the name on the line, or when the last lookup failed. Does nothing on a call with no C360 line. Never throws.
 */
export async function onC360CustomerConfirmed(
    sessionId: string,
    customer: { mdn: string; fullName: string },
): Promise<void> {
    try {
        const partner = useSessionStore.getState().getSession(sessionId)?.currentTask?.partner;
        if (!partner) return;
        const customerName = customer.fullName.trim() || undefined;
        const store = useC360CustomerStore.getState();
        store.updateSession(sessionId, { customerName });
        const current = store.getSession(sessionId);
        const mdn = toC360Mdn(customer.mdn) ?? current?.searchedMdn;
        if (!mdn) return;
        // A lookup of the same number still running picks the name up when it finishes.
        if (current?.status === "searching" && current.searchedMdn === mdn) return;
        // A failed lookup is tried again, even for the same number and name.
        if (current?.status === "failed" || current?.searchedMdn !== mdn || current.searchedName !== customerName) {
            await linkCustomerAndLoadHistory(sessionId, partner, "confirm", mdn, customerName);
        }
    } catch (error) {
        logger.warn({ error, sessionId }, "C360 customer confirm handling failed");
    }
}

/**
 * A recorded call whose number wasn't looked up, counted with the link outcomes so the dashboard still shows it. The
 * outcome only: the number is customer data.
 */
function recordSkippedLookup(sessionId: string, partner: Partner, outcome: "blocklisted" | "no_mdn"): void {
    // Only the call-start lookup skips, so its number is always the IVR's.
    void sendEventMetric("C360LinkCustomer", { ...metricDimensions(partner), outcome, source: "ivr" });
    logger.debug({ sessionId, partner, c360LinkOutcome: outcome }, "C360 customer link skipped");
}

/** Forgets the session's C360 lookup when it ends. */
export function clearC360CustomerSession(sessionId: string): void {
    useC360CustomerStore.getState().removeSession(sessionId);
}

async function linkCustomerAndLoadHistory(
    sessionId: string,
    partner: Partner,
    source: C360LookupSource,
    mdn: string,
    customerName?: string,
): Promise<void> {
    // Read while waiting on the line, rather than after it, so the lookup doesn't wait on the two in turn.
    const includeHistoryRead = isC360FlagOn("isC360CustomerHistoryEnabled", partner);
    const line = await getC360Line(sessionId);
    if (!line) return;
    const previous = useC360CustomerStore.getState().getSession(sessionId);
    const name = customerName ?? previous?.customerName;
    // C360 finds a customer's history by their number alone, so looking the same number up again (to put a confirmed
    // name on the line) would return the same history at the cost of another summary call per subscription.
    const reuseHistory =
        previous?.searchedMdn === mdn && previous.historyRequested === true && previous.history !== undefined;
    useC360CustomerStore.getState().setSession(sessionId, {
        status: "searching",
        searchedMdn: mdn,
        searchedName: name,
        customerName: name,
        // The flag decision isn't the customer's, so it's kept for any number until this lookup reads the flag.
        historyRequested: previous?.historyRequested,
        // Kept while the same number is looked up again, so the panel doesn't empty and refill; another number is
        // another customer, whose history mustn't show under this one's.
        history: previous?.searchedMdn === mdn ? previous.history : undefined,
    });
    // Awaited after the lookup is marked searching, so a confirm arriving meanwhile still waits on this lookup. Each
    // subscription's history is a C360 model call, so it's only asked for when the panel will show it.
    const historyFlagOn = await includeHistoryRead;
    const latest = useC360CustomerStore.getState().getSession(sessionId);
    // A confirm for another number, or the call ending, replaced this lookup while the flag was read. Sending it now
    // could link the line to the wrong customer, which no later check on the response can undo.
    if (latest?.searchedMdn !== mdn || latest.searchedName !== name) return;
    const includeHistory = historyFlagOn && !reuseHistory;
    useC360CustomerStore.getState().updateSession(sessionId, { historyRequested: historyFlagOn });
    const result = await withMetrics(
        "C360LinkCustomer",
        () =>
            linkCustomerAndGetHistory({
                sessionId,
                partner,
                interactionLineId: line.interactionLineId,
                mdn,
                customerName: name,
                includeHistory,
            }),
        { logger, rethrow: false, logResult: false, dimensions: metricDimensions(partner) },
    );
    const current = useC360CustomerStore.getState().getSession(sessionId);
    // A confirm with another number started a newer lookup; its result wins.
    if (current?.searchedMdn !== mdn || current.searchedName !== name) return;
    if (!result) {
        // withMetrics logs the error without the session ID.
        logger.warn({ sessionId, partner, interactionLineId: line.interactionLineId }, "C360 customer link failed");
        // Any history kept is this number's, so it stays on screen rather than be replaced by a failure.
        useC360CustomerStore.getState().updateSession(sessionId, { status: "failed" });
        // A confirm that arrived while this ran left its name for this lookup, so it's tried again with the name.
        if (current.customerName !== name) {
            await linkCustomerAndLoadHistory(sessionId, partner, "confirm", mdn, current.customerName);
        }
        return;
    }
    const { subscriptionCount, historyComplete, interactions } = result;
    const history = reuseHistory ? current.history : { subscriptionCount, historyComplete, interactions };
    useC360CustomerStore.getState().updateSession(sessionId, { status: result.outcome, history });
    void sendEventMetric("C360LinkCustomer", { ...metricDimensions(partner), outcome: result.outcome, source });
    logger.info(
        {
            sessionId,
            partner,
            interactionLineId: line.interactionLineId,
            c360LinkOutcome: result.outcome,
            // The history the panel has, which a reused lookup didn't get from this response.
            c360SubscriptionsFound: history?.subscriptionCount,
            c360InteractionsFound: history?.interactions.length,
            c360HistoryComplete: history?.historyComplete,
            c360HistoryRequested: includeHistory,
            c360HistoryReused: reuseHistory,
        },
        "C360 customer link",
    );
    // A confirm that arrived while this ran left a name for the line.
    if (result.outcome === "linked" && current.customerName !== name) {
        await linkCustomerAndLoadHistory(sessionId, partner, "confirm", mdn, current.customerName);
    }
}
